import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Badge, Button, Modal, Form, Alert, Spinner } from 'react-bootstrap';
import { apiService } from '../../services/api';

interface ParkingSpace {
  id: string;
  number: string;
  status: 'available' | 'occupied' | 'reserved' | 'maintenance';
  vehicle?: {
    plateNumber: string;
    entryTime: string;
  };
  floor: string;
  zone: string;
}

const ParkingSpaces: React.FC = () => {
  const [spaces, setSpaces] = useState<ParkingSpace[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [showModal, setShowModal] = useState(false);
  const [selectedSpace, setSelectedSpace] = useState<ParkingSpace | null>(null);
  const [plateNumber, setPlateNumber] = useState('');

  // 載入停車位數據
  const loadParkingSpaces = async () => {
    try {
      setLoading(true);
      setError('');
      
      const response = await apiService.getParkingSpaces();
      
      if (response.success && response.data) {
        // 轉換API數據格式到前端格式
        const formattedSpaces: ParkingSpace[] = response.data.items?.map((space: any) => ({
          id: space._id || space.id,
          number: space.spotNumber || space.number,
          status: space.status,
          floor: space.floor || '1F',
          zone: space.zone || space.area,
          vehicle: space.currentVehicle ? {
            plateNumber: space.currentVehicle.licensePlate || space.currentVehicle.plateNumber,
            entryTime: space.currentVehicle.entryTime || new Date().toISOString()
          } : undefined
        })) || [];
        
        setSpaces(formattedSpaces);
      } else {
        setError('載入停車位數據失敗');
      }
    } catch (error: any) {
      console.error('載入停車位失敗:', error);
      setError('載入停車位數據失敗，請稍後再試');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadParkingSpaces();
  }, []);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'available': return 'success';
      case 'occupied': return 'danger';
      case 'reserved': return 'warning';
      case 'maintenance': return 'secondary';
      default: return 'secondary';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'available': return '空閒';
      case 'occupied': return '已佔用';
      case 'reserved': return '已預約';
      case 'maintenance': return '維護中';
      default: return '未知';
    }
  };

  const handleSpaceClick = (space: ParkingSpace) => {
    setSelectedSpace(space);
    setPlateNumber(space.vehicle?.plateNumber || '');
    setShowModal(true);
  };

  const handleAssignVehicle = async () => {
    if (!selectedSpace || !plateNumber.trim()) return;

    try {
      // 這裡應該調用API來分配車輛到停車位
      const updatedSpaces = spaces.map(space => 
        space.id === selectedSpace.id 
          ? { 
              ...space, 
              status: 'occupied' as const, 
              vehicle: { 
                plateNumber: plateNumber.trim(), 
                entryTime: new Date().toLocaleString() 
              } 
            }
          : space
      );
      
      setSpaces(updatedSpaces);
      setShowModal(false);
      setPlateNumber('');
      setSelectedSpace(null);
    } catch (error) {
      console.error('分配停車位失敗:', error);
    }
  };

  const handleReleaseSpace = async () => {
    if (!selectedSpace) return;

    try {
      // 這裡應該調用API來釋放停車位
      const updatedSpaces = spaces.map(space => 
        space.id === selectedSpace.id 
          ? { ...space, status: 'available' as const, vehicle: undefined }
          : space
      );
      
      setSpaces(updatedSpaces);
      setShowModal(false);
      setSelectedSpace(null);
    } catch (error) {
      console.error('釋放停車位失敗:', error);
    }
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
        <Spinner animation="border" role="status">
          <span className="visually-hidden">載入中...</span>
        </Spinner>
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="danger" className="text-center">
        <i className="fas fa-exclamation-triangle me-2"></i>
        {error}
      </Alert>
    );
  }

  const availableCount = spaces.filter(s => s.status === 'available').length;
  const occupiedCount = spaces.filter(s => s.status === 'occupied').length;
  const reservedCount = spaces.filter(s => s.status === 'reserved').length;
  const maintenanceCount = spaces.filter(s => s.status === 'maintenance').length;

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h1>
          <i className="fas fa-car me-3"></i>
          停車位管理
        </h1>
        <Button variant="primary" onClick={loadParkingSpaces}>
          <i className="fas fa-sync-alt me-2"></i>
          重新整理
        </Button>
      </div>

      {/* 統計卡片 */}
      <Row className="mb-4">
        <Col xs={12} sm={6} lg={3} className="mb-3">
          <Card className="text-center">
            <Card.Body>
              <h3 className="text-success">{availableCount}</h3>
              <p className="mb-0">空閒車位</p>
            </Card.Body>
          </Card>
        </Col>
        <Col xs={12} sm={6} lg={3} className="mb-3">
          <Card className="text-center">
            <Card.Body>
              <h3 className="text-danger">{occupiedCount}</h3>
              <p className="mb-0">已佔用</p>
            </Card.Body>
          </Card>
        </Col>
        <Col xs={12} sm={6} lg={3} className="mb-3">
          <Card className="text-center">
            <Card.Body>
              <h3 className="text-warning">{reservedCount}</h3>
              <p className="mb-0">已預約</p>
            </Card.Body>
          </Card>
        </Col>
        <Col xs={12} sm={6} lg={3} className="mb-3">
          <Card className="text-center">
            <Card.Body>
              <h3 className="text-secondary">{maintenanceCount}</h3>
              <p className="mb-0">維護中</p>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* 停車位網格 */}
      <Card>
        <Card.Header>
          <h5 className="mb-0">停車位狀態</h5>
        </Card.Header>
        <Card.Body>
          <Row>
            {spaces.map((space) => (
              <Col xs={6} sm={4} md={3} lg={2} key={space.id} className="mb-3">
                <Card 
                  className={`text-center cursor-pointer border-${getStatusColor(space.status)}`}
                  style={{ cursor: 'pointer' }}
                  onClick={() => handleSpaceClick(space)}
                >
                  <Card.Body className="py-2">
                    <div className="fw-bold">{space.number}</div>
                    <Badge bg={getStatusColor(space.status)} className="mb-1">
                      {getStatusText(space.status)}
                    </Badge>
                    {space.vehicle && (
                      <div className="small text-muted">
                        {space.vehicle.plateNumber}
                      </div>
                    )}
                    <div className="small text-muted">
                      {space.floor} - {space.zone}區
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        </Card.Body>
      </Card>

      {/* 停車位詳情模態框 */}
      <Modal show={showModal} onHide={() => setShowModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>停車位 {selectedSpace?.number}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedSpace && (
            <div>
              <p><strong>位置:</strong> {selectedSpace.floor} - {selectedSpace.zone}區</p>
              <p><strong>狀態:</strong> <Badge bg={getStatusColor(selectedSpace.status)}>{getStatusText(selectedSpace.status)}</Badge></p>
              
              {selectedSpace.status === 'occupied' && selectedSpace.vehicle && (
                <div>
                  <p><strong>車牌號碼:</strong> {selectedSpace.vehicle.plateNumber}</p>
                  <p><strong>進入時間:</strong> {selectedSpace.vehicle.entryTime}</p>
                </div>
              )}

              {selectedSpace.status === 'available' && (
                <Form>
                  <Form.Group className="mb-3">
                    <Form.Label>車牌號碼</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="請輸入車牌號碼"
                      value={plateNumber}
                      onChange={(e) => setPlateNumber(e.target.value)}
                    />
                  </Form.Group>
                </Form>
              )}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            取消
          </Button>
          {selectedSpace?.status === 'available' && (
            <Button variant="primary" onClick={handleAssignVehicle} disabled={!plateNumber.trim()}>
              分配車輛
            </Button>
          )}
          {selectedSpace?.status === 'occupied' && (
            <Button variant="danger" onClick={handleReleaseSpace}>
              釋放車位
            </Button>
          )}
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default ParkingSpaces;